import { includes } from 'lodash';
import { useCallback } from 'react';
import { create } from 'zustand';

import type { SoftwareUpdateData } from '@sb/feathers-types';
import { makeNamespacedLog } from '@sb/log';
import { wait } from '@sb/utilities';
import {
  getSoftwareUpdateData,
  requestSoftwareInstall,
  requestCancelDownload,
  requestSwitchToOffline,
  markWarningAsNotified as markWarningAsNotifiedService,
} from '@sbrc/services';

const log = makeNamespacedLog('useSoftwareBuildData');
// This BUILD_ID is the same marvin Build ID.
// The Github build workflow will pass this as an argument to the docker file.
// That way the build ID gets "baked in" to the image.
// For local development this won't be set, so we use 'active' as the default.
// 'active' matches the default build ID in local-build-data/read-and-write.ts
export const BUILD_ID = import.meta.env.VITE_BUILD_ID || 'active';

export const RELOAD_GUARD_KEY = 'buildMismatchReloadTimestamp';
const RELOAD_COOLDOWN_MS = 30_000;

export interface SoftwareBuildData extends Partial<SoftwareUpdateData> {
  timeChecked: Date | null;
  hasRequestedSoftwareInstall: boolean;
  isInstallingSoftware: boolean;
  onRequestSoftwareInstall: (
    offlineUpdatePath?: string,
    buildId?: string,
    buildName?: string,
  ) => void;
  onCancelDownload: (buildId?: string) => void;
  /**
   * Switch to the USB build when an online download is blocking the offline
   * auto-loader: cancels the online download and records the offline selection
   * so it can't be re-adopted until installed or the drive is removed.
   */
  onSwitchToOffline: (onlineBuildId: string, offlineUpdatePath: string) => void;
  isOnlineSoftwareUpdateAvailable: boolean;
  markWarningAsNotified: () => Promise<void>;
  // Map of joint number to connection status (only populated when HardwareConfig is enabled)
  jointConnectionStatus?: Map<number, 'disconnected' | 'connected' | 'ready'>;
  // True when a build mismatch persists after a cache-busting reload attempt
  buildMismatchDetected: boolean;
}

const INITIAL_BUILD_DATA: SoftwareBuildData = {
  timeChecked: null,
  hasRequestedSoftwareInstall: false,
  isInstallingSoftware: false,
  onRequestSoftwareInstall: () => {},
  onCancelDownload: () => {},
  onSwitchToOffline: () => {},
  isOnlineSoftwareUpdateAvailable: false,
  lastErrorMessage: undefined,
  lastErrorAt: undefined,
  lastWarningMessage: undefined,
  lastWarningNotified: undefined,
  markWarningAsNotified: async () => {},
  downloadNetworkInterface: undefined,
  buildMismatchDetected: false,
};

const useBuildData = create(() => INITIAL_BUILD_DATA);

/**
 * poll backend for changes in software build data
 */
export const pollSoftwareBuildData = async () => {
  while (true) {
    try {
      const softwareUpdateData = await getSoftwareUpdateData();

      let { hasRequestedSoftwareInstall } = useBuildData.getState();

      if (
        !softwareUpdateData ||
        softwareUpdateData.isSoftwareInstallRequested ||
        softwareUpdateData.status === 'installing' ||
        softwareUpdateData.status === 'installed'
      ) {
        hasRequestedSoftwareInstall = false;
      }

      const activeBuildID =
        softwareUpdateData?.activeBuildID ??
        // if undefined then feathers-bot is running a different version than remote-control
        // (i.e. during upgrade)
        'version-mismatch';

      const activeBuildName = softwareUpdateData?.activeBuildName ?? '';
      const isLocalDevBuild = activeBuildName.startsWith('/local-dev/');
      // K8s sim-bot pods seed active_build.json with name `k8s-<tag>`
      // (see apps/api-srv/src/integrations/k8s/podSpec.ts). Every
      // container in the pod is built from the same image so the
      // BUILD_ID baked into the bundle can't disagree with the api-srv
      // side in any meaningful way — the mismatch the reload guards
      // against is impossible here, and the reload races the Feathers
      // Socket.IO handshake which is what we want to avoid.
      const isK8sSimBuild = activeBuildName.startsWith('k8s-');

      // Reload if the active build ID is different from the BUILD_ID
      // This is to ensure that the remote-control is always running the same build as the one
      // that is being installed.
      if (BUILD_ID !== activeBuildID) {
        if (BUILD_ID === 'active' || isLocalDevBuild || isK8sSimBuild) {
          // if BUILD_ID is 'active', the active build is a /local-dev/,
          // or this is a K8s sim-bot session, and BUILD_ID doesn't
          // match the activeBuildID, then either a developer is running
          // a custom remote-control build on a robot, or the pod is a
          // K8s sim where every container is the same image. Either
          // way the reload would be pointless.
        } else {
          // Otherwise, this is a production environment and we should reload the page
          // to ensure that the remote-control is always running the same build as the one
          // that is being installed.
          log.info(
            'pollSoftwareBuildData',
            `build ID mismatch: remote-control '${BUILD_ID}' !== active_build.json '${activeBuildID}'`,
          );

          const lastReload = parseInt(
            sessionStorage.getItem(RELOAD_GUARD_KEY) ?? '0',
            10,
          );

          if (Date.now() - lastReload < RELOAD_COOLDOWN_MS) {
            log.error(
              'pollSoftwareBuildData',
              'Build ID mismatch persists after reload — aborting to prevent infinite loop. ' +
                'Clear browser cache or hard-refresh (Ctrl+Shift+R) to load the latest version.',
            );

            useBuildData.setState({ buildMismatchDetected: true });

            break;
          }

          sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));

          // Use cache-busting navigation instead of window.location.reload()
          // so the browser treats it as a new URL and bypasses the HTTP cache.
          const url = new URL(window.location.href);
          url.searchParams.set('_cb', String(Date.now()));
          window.location.replace(url.toString());

          return;
        }
      }

      // Replace rather than merge. `find()` omits optional fields entirely when
      // they are unset — JSON has no `undefined` — so "there is no offline
      // update" arrives as an absent key, not an explicit null. Merging treats
      // that absence as "leave the previous value alone", which strands stale
      // values in the store: an `offlineUpdatePath` from a USB target that has
      // since been replaced by an online one never clears, and the panels then
      // derive the wrong state from it. The payload below is a complete
      // snapshot, so applying it wholesale is the correct semantics.
      //
      // Consequence for future edits: any state written here by a separate
      // `setState` call will be discarded by the next poll unless it also
      // appears in INITIAL_BUILD_DATA or the server payload.
      useBuildData.setState(
        {
          ...INITIAL_BUILD_DATA,
          ...softwareUpdateData,
          timeChecked: new Date(),
          hasRequestedSoftwareInstall,
        },
        true,
      );

      await wait(1_000);
    } catch (error) {
      log.warn('poll.fail', 'Failed to get SoftwareUpdateData', error);
      await wait(2_000);
    }

    await wait(1_000);
  }
};

/**
 * State the online-update section derives from the shared target slot.
 *
 * Online and offline updates occupy one `target_build.json` entry, so an offline
 * build in the slot has to be recognised before any online status is shown.
 * Both the legacy panel and the redesigned section render this, so the
 * derivation lives here rather than being duplicated per panel.
 */
export interface OnlineUpdateState {
  /** An offline (USB) build currently occupies the target slot. */
  isOfflineUpdateInProgress: boolean;
  /** Status to attribute to the online update, or undefined while offline owns the slot. */
  onlineStatus: SoftwareUpdateData['status'] | undefined;
  /**
   * An offline build occupies the slot and has not finished installing. The slot
   * keeps its `offlineUpdatePath` after a successful install, so occupancy alone
   * would report an offline update in progress indefinitely.
   */
  isOfflineUpdateActive: boolean;
  /** An install is running for the online target rather than an offline one. */
  isOnlineInstalling: boolean;
}

export function deriveOnlineUpdateState(
  buildData: SoftwareBuildData,
): OnlineUpdateState {
  const { isInstallingSoftware, status, targetBuildID, offlineUpdatePath } =
    buildData;

  const isOfflineUpdateInProgress = includes(offlineUpdatePath, targetBuildID);

  return {
    isOfflineUpdateInProgress,
    onlineStatus: isOfflineUpdateInProgress ? undefined : status,
    isOfflineUpdateActive: isOfflineUpdateInProgress && status !== 'installed',
    isOnlineInstalling: isInstallingSoftware && !isOfflineUpdateInProgress,
  };
}

export const useSoftwareBuildData = (): SoftwareBuildData => {
  const buildData = useBuildData();

  const onRequestSoftwareInstall = useCallback(
    async (
      offlineUpdatePath?: string,
      buildId?: string,
      buildName?: string,
    ) => {
      useBuildData.setState({ hasRequestedSoftwareInstall: true });

      await requestSoftwareInstall(offlineUpdatePath, buildId, buildName);
    },
    [],
  );

  const onCancelDownload = useCallback(async (buildId?: string) => {
    try {
      await requestCancelDownload(buildId);
    } catch (error) {
      log.warn(
        'onCancelDownload.fail',
        'Failed to request cancel download on backend',
        error,
      );
    }
  }, []);

  const onSwitchToOffline = useCallback(
    async (onlineBuildId: string, offlineUpdatePath: string) => {
      try {
        await requestSwitchToOffline(onlineBuildId, offlineUpdatePath);
      } catch (error) {
        log.warn(
          'onSwitchToOffline.fail',
          'Failed to request switch to offline update on backend',
          error,
        );
      }
    },
    [],
  );

  const markWarningAsNotified = useCallback(async () => {
    useBuildData.setState({ lastWarningNotified: true });

    // Also update the backend to persist the change
    try {
      await markWarningAsNotifiedService();
    } catch (error) {
      log.warn(
        'markWarningAsNotified.fail',
        'Failed to mark warning as notified on backend',
        error,
      );
    }
  }, []);

  // An offline install that has failed at least once but hasn't hit the retry
  // cap is being auto-retried by updater-bot in the background. The target
  // status is 'pending' between attempts, which looks identical to an idle
  // state — we treat it as "installing" so the UI keeps the install button
  // disabled until retries either succeed or exhaust.
  const isOfflineRetryInProgress =
    buildData.offlineUpdatePath !== undefined &&
    buildData.status === 'pending' &&
    (buildData.offlineInstallFailureCount ?? 0) > 0 &&
    !buildData.isOfflineInstallRetryExhausted;

  const isInstallingSoftware =
    buildData.hasRequestedSoftwareInstall ||
    buildData.isSoftwareInstallRequested ||
    buildData.status === 'installing' ||
    isOfflineRetryInProgress;

  let isOnlineSoftwareUpdateAvailable: boolean =
    buildData.targetBuildID !== 'Unknown' &&
    buildData.targetBuildID !== buildData.activeBuildID &&
    !includes(buildData.offlineUpdatePath, buildData.targetBuildID);

  const newOperationMode =
    buildData.targetOperationMode !== undefined &&
    buildData.targetOperationMode !== buildData.activeOperationMode;

  if (newOperationMode) {
    isOnlineSoftwareUpdateAvailable = true;
  }

  return {
    ...buildData,
    onRequestSoftwareInstall,
    onCancelDownload,
    onSwitchToOffline,
    isInstallingSoftware,
    isOnlineSoftwareUpdateAvailable,
    markWarningAsNotified,
  };
};
